import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { A2ASocketService } from './services/a2a-socket.service';
import {
  AgentConfig,
  AgentDirectoryEntry,
  AgentId,
  ConversationState,
  DirectMessage,
  SystemLog
} from './services/api.types';

interface VisualNode {
  id: string;
  title: string;
  subtitle: string;
  body: string;
  type: 'dashboard' | 'directory' | 'channel' | 'decision' | 'agent' | 'external' | 'messages';
  status: 'online' | 'down' | 'connected' | 'idle';
  x: number;
  y: number;
  w: number;
  h: number;
  agentId?: AgentId;
  compact?: boolean;
}

interface VisualConnection {
  id: string;
  from: string;
  to: string;
  label: string;
  kind: 'auth' | 'http' | 'agent' | 'db' | 'event';
  active: boolean;
  count?: number;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly productionOrigin = 'https://agent-management.egm-robotics.com';
  private readonly apiKeyStorageKey = 'a2a_management_openai_api_key';
  private readonly settingsStorageKey = 'a2a_management_settings';

  topic = 'Gestionar una tarea usando directorio A2A, decision y mensajes directos.';
  apiKey = '';
  auto = false;
  maxTurns = 12;
  starter: AgentId = 'agent-a';
  socketStatus = 'disconnected';
  busy = false;
  error = '';
  state: ConversationState | null = null;
  selectedAgentId: AgentId | null = null;
  filterText = '';
  detailsMode: 'agent' | 'events' = 'agent';
  recentEvents: Array<{ id: string; agentId: AgentId; name: string; action: 'connected' | 'disconnected'; time: Date }> = [];
  private prevConnectedIds = new Set<AgentId>();
  private lastSeenAt = new Map<AgentId, Date>();
  private lastKnownAgents = new Map<AgentId, AgentDirectoryEntry>();
  private layoutSlots = new Map<AgentId, number>();
  private nextLayoutSlot = 0;
  private readonly disconnectedRetentionMs = 3 * 60 * 60 * 1000;
  private readonly orbitBaseRadius = 460;
  zoom = 0.56;
  panX = 100;
  panY = 80;
  private panning = false;
  private lastPointer = { x: 0, y: 0 };

  agents: AgentConfig[] = [
    {
      id: 'agent-a',
      name: 'Agente Backend',
      description: 'Especialista en backend, contratos de mensajes y servicios internos.',
      instruction: 'Resuelve solo temas de backend, APIs, seguridad, datos y comunicacion entre servicios.',
      delayMs: 1200
    },
    {
      id: 'agent-b',
      name: 'Agente Frontend',
      description: 'Especialista en frontend, experiencia visual y control de interfaces.',
      instruction: 'Resuelve solo temas de frontend, UX, pantallas y visualizacion de estados.',
      delayMs: 1800
    }
  ];

  infoLinks = [
    { label: 'Directorio', href: `${this.resourcesOrigin()}/resources/agent-directory.html` },
    { label: 'Monitor A2A', href: `${this.resourcesOrigin()}/resources/index.html` },
    { label: 'Contrato A2A', href: `${this.resourcesOrigin()}/resources/a2a-contracts.html` },
    { label: 'Guia WS', href: `${this.resourcesOrigin()}/resources/a2a-transport.html` },
    { label: 'Consola WS', href: `${this.resourcesOrigin()}/resources/ws-console.html` }
  ];

  private subscriptions = new Subscription();

  constructor(private readonly socket: A2ASocketService) {}

  private resourcesOrigin() {
    const isLocalAngular = ['localhost', '127.0.0.1'].includes(location.hostname) && location.port === '4200';
    return isLocalAngular ? this.productionOrigin : location.origin;
  }

  ngOnInit() {
    this.loadSettings();
    this.apiKey = localStorage.getItem(this.apiKeyStorageKey) ?? '';
    this.subscriptions.add(this.socket.state$.subscribe((state) => {
      this.state = state;
      this.trackConnectionEvents();
      this.ensureSelectedAgent();
    }));
    this.subscriptions.add(this.socket.status$.subscribe((status) => (this.socketStatus = status)));
    this.subscriptions.add(this.socket.error$.subscribe((error) => (this.error = error)));
    this.socket.connect();
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => this.resetView());
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.resetView();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.socket.disconnect();
  }

  loadSettings() {
    const raw = localStorage.getItem(this.settingsStorageKey);
    if (!raw) return;

    try {
      const settings = JSON.parse(raw) as {
        topic?: string;
        auto?: boolean;
        maxTurns?: number;
        starter?: AgentId;
        agents?: AgentConfig[];
      };

      this.topic = settings.topic ?? this.topic;
      this.auto = settings.auto ?? this.auto;
      this.maxTurns = settings.maxTurns ?? this.maxTurns;
      this.starter = settings.starter ?? this.starter;

      if (settings.agents?.length === 2) {
        this.agents = this.agents.map((fallback) => ({
          ...fallback,
          ...settings.agents?.find((agent) => agent.id === fallback.id),
          description: settings.agents?.find((agent) => agent.id === fallback.id)?.description ?? fallback.description
        }));
      }
    } catch {
      localStorage.removeItem(this.settingsStorageKey);
    }
  }

  saveSettings() {
    localStorage.setItem(this.settingsStorageKey, JSON.stringify({
      topic: this.topic,
      auto: this.auto,
      maxTurns: Number(this.maxTurns),
      starter: this.starter,
      agents: this.agents.map((agent) => ({
        ...agent,
        delayMs: Number(agent.delayMs)
      }))
    }));
  }

  saveApiKey() {
    const value = this.apiKey.trim();
    if (value) {
      localStorage.setItem(this.apiKeyStorageKey, value);
      return;
    }

    localStorage.removeItem(this.apiKeyStorageKey);
  }

  start() {
    this.busy = true;
    this.error = '';
    this.saveApiKey();
    this.saveSettings();
    this.socket.startConversation({
      topic: this.topic,
      auto: this.auto,
      maxTurns: Number(this.maxTurns),
      starter: this.starter,
      apiKey: this.apiKey.trim(),
      agents: this.agents.map((agent) => ({
        ...agent,
        delayMs: Number(agent.delayMs)
      }))
    });
    this.busy = false;
  }

  finish() {
    this.socket.finishConversation();
  }

  setAuto(auto: boolean) {
    this.auto = auto;
    this.saveSettings();
    this.socket.setAuto(auto);
  }

  step() {
    this.socket.stepConversation();
  }

  startAgent(agentId: AgentId) {
    const agent = this.agents.find((item) => item.id === agentId);
    if (!agent) return;
    this.saveSettings();
    this.socket.startAgent({
      ...agent,
      delayMs: Number(agent.delayMs)
    });
  }

  stopAgent(agentId: AgentId) {
    this.socket.stopAgent(agentId);
  }

  connectChannel() {
    this.socket.connectChannel();
  }

  disconnectChannel() {
    this.socket.disconnectChannel();
  }

  zoomIn() {
    this.zoom = Math.min(1.8, Number((this.zoom + 0.1).toFixed(2)));
  }

  zoomOut() {
    this.zoom = Math.max(0.28, Number((this.zoom - 0.1).toFixed(2)));
  }

  resetView() {
    this.zoom = 0.56;
    this.centerStageOn(this.hubCX, this.hubCY);
  }

  fitView() {
    const nodes = this.graphNodes();
    const bounds = nodes.reduce(
      (box, node) => ({
        minX: Math.min(box.minX, node.x),
        minY: Math.min(box.minY, node.y),
        maxX: Math.max(box.maxX, node.x + node.w),
        maxY: Math.max(box.maxY, node.y + node.h)
      }),
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
    );

    const viewport = this.visibleMapViewport();
    const graphWidth = bounds.maxX - bounds.minX;
    const graphHeight = bounds.maxY - bounds.minY;
    const padding = 60;
    const zoomX = (viewport.width - padding * 2) / graphWidth;
    const zoomY = (viewport.height - padding * 2) / graphHeight;

    this.zoom = Math.max(0.28, Math.min(1.1, Number(Math.min(zoomX, zoomY).toFixed(2))));
    this.centerStageOn(bounds.minX + graphWidth / 2, bounds.minY + graphHeight / 2);
  }

  onCanvasWheel(event: WheelEvent) {
    event.preventDefault();
    const delta = event.deltaY > 0 ? -0.06 : 0.06;
    this.zoom = Math.max(0.28, Math.min(1.8, Number((this.zoom + delta).toFixed(2))));
  }

  startPan(event: MouseEvent) {
    if ((event.target as HTMLElement).closest('button, input, textarea, select, .map-node')) return;
    this.panning = true;
    this.lastPointer = { x: event.clientX, y: event.clientY };
  }

  movePan(event: MouseEvent) {
    if (!this.panning) return;
    this.panX += event.clientX - this.lastPointer.x;
    this.panY += event.clientY - this.lastPointer.y;
    this.lastPointer = { x: event.clientX, y: event.clientY };
  }

  endPan() {
    this.panning = false;
  }

  agentName(agentId: AgentId | null | undefined) {
    if (!agentId) return '';
    return this.agentEntry(agentId)?.name ?? this.state?.agents[agentId]?.name ?? this.agents.find((agent) => agent.id === agentId)?.name ?? agentId;
  }

  isOnline(agentId: AgentId) {
    return this.agentEntry(agentId)?.connected ?? this.state?.runtime?.[agentId] === 'online';
  }

  activity(agentId: AgentId) {
    return this.agentEntry(agentId)?.activity ?? this.state?.activity?.[agentId] ?? (this.isOnline(agentId) ? 'idle' : 'desconectado');
  }

  selectAgent(agentId: AgentId) {
    this.selectedAgentId = agentId;
    this.detailsMode = 'agent';
  }

  visibleAgents() {
    const query = this.filterText.trim().toLowerCase();
    const agents = this.allAgents();
    if (!query) return agents;

    return agents.filter((agent) => [
      agent.agentId,
      agent.name,
      agent.description,
      agent.specialty,
      agent.purpose,
      agent.connectionId
    ].some((value) => String(value ?? '').toLowerCase().includes(query)));
  }

  allAgents(): AgentDirectoryEntry[] {
    const byId = new Map<AgentId, AgentDirectoryEntry>();
    const add = (agent: AgentDirectoryEntry) => byId.set(agent.agentId, { ...byId.get(agent.agentId), ...agent });

    this.state?.directory.available?.forEach(add);
    this.state?.directory.connected?.forEach(add);

    for (const config of this.agents) {
      if (byId.has(config.id)) continue;
      add({
        agentId: config.id,
        connectionId: null,
        ownerUserId: null,
        name: this.state?.agents?.[config.id]?.name ?? config.name,
        specialty: config.description,
        description: this.state?.agents?.[config.id]?.description ?? config.description,
        purpose: config.instruction,
        status: this.state?.runtime?.[config.id] ?? 'down',
        activity: this.state?.activity?.[config.id] ?? (this.state?.runtime?.[config.id] === 'online' ? 'idle' : 'desconectado'),
        connected: this.state?.runtime?.[config.id] === 'online',
        available: this.state?.runtime?.[config.id] === 'online',
        transport: 'websocket'
      });
    }

    return [...byId.values()].sort((left, right) => {
      const statusScore = Number(right.connected) - Number(left.connected);
      if (statusScore) return statusScore;
      return left.name.localeCompare(right.name);
    });
  }

  connectedAgents() {
    return this.allAgents().filter((agent) => agent.connected);
  }

  availableAgents() {
    return this.allAgents().filter((agent) => agent.available);
  }

  externalAgents() {
    const local = new Set(this.agents.map((agent) => agent.id));
    return this.connectedAgents().filter((agent) => !local.has(agent.agentId));
  }

  selectedAgent() {
    if (!this.selectedAgentId) return this.connectedAgents()[0] ?? this.allAgents()[0] ?? null;
    return this.agentEntry(this.selectedAgentId) ?? null;
  }

  agentEntry(agentId: AgentId) {
    return this.allAgents().find((agent) => agent.agentId === agentId) ?? this.lastKnownAgents.get(agentId) ?? null;
  }

  latestMessages(): DirectMessage[] {
    return [...(this.state?.directMessages ?? [])].slice(-8).reverse();
  }

  latestLogs(): SystemLog[] {
    return [...(this.state?.logs ?? [])].slice(-10).reverse();
  }

  agentMessages(agentId: AgentId) {
    return this.latestMessages().filter((message) => message.from === agentId || message.to === agentId);
  }

  agentQueries(agentId: AgentId) {
    return [...(this.state?.directoryQueries ?? [])]
      .filter((query) => query.requester === agentId || query.selectedAgent === agentId)
      .slice(-5)
      .reverse();
  }

  directMessageCount(agentId: AgentId) {
    return (this.state?.directMessages ?? []).filter((message) => message.from === agentId || message.to === agentId).length;
  }

  connectionLabel(agent: AgentDirectoryEntry) {
    if (agent.connectionId) return agent.connectionId;
    return agent.connected ? 'server-local' : 'sin direccion activa';
  }

  compact(value: string | null | undefined, limit = 72) {
    const normalized = String(value ?? '').replace(/\s+/g, ' ').trim();
    return normalized.length > limit ? `${normalized.slice(0, limit)}...` : normalized;
  }

  initials(name: string) {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'A';
  }

  copy(text: string | null | undefined) {
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(() => undefined);
  }

  trackByAgent(_index: number, agent: AgentDirectoryEntry) {
    return agent.agentId;
  }

  trackByMessage(_index: number, message: { id: string }) {
    return message.id;
  }

  trackByNode(_index: number, node: VisualNode) {
    return node.id;
  }

  trackByConnection(_index: number, connection: VisualConnection) {
    return connection.id;
  }

  filteredAgentsForDisplay(): AgentDirectoryEntry[] {
    const cutoff = Date.now() - this.disconnectedRetentionMs;
    const byId = new Map<AgentId, AgentDirectoryEntry>();

    for (const agent of this.lastKnownAgents.values()) {
      byId.set(agent.agentId, agent);
    }

    for (const agent of this.allAgents()) {
      byId.set(agent.agentId, agent);
    }

    return [...byId.values()].filter((agent) =>
      agent.connected ||
      (this.lastSeenAt.has(agent.agentId) && this.lastSeenAt.get(agent.agentId)!.getTime() > cutoff)
    ).sort((left, right) => this.agentSlot(left.agentId) - this.agentSlot(right.agentId));
  }

  graphNodes(): VisualNode[] {
    const agents = this.filteredAgentsForDisplay();
    const visibleCount = Math.max(agents.length, 1);

    const nodeW = 204;
    const nodeH = 86;
    const gap = 28;
    const radiusForSpacing = Math.ceil((visibleCount * (nodeW + gap)) / (2 * Math.PI));
    const radius = Math.max(this.orbitBaseRadius, radiusForSpacing);

    // Hub is 480×300; agents at 30°/150° need radius > (hubHH + nodeHH + gap) / sin(30°) = (150+43+20)/0.5 = 426

    const cx = this.hubCX;
    const cy = this.hubCY;

    const nodes: VisualNode[] = [
      {
        id: 'directory',
        title: 'IA Directory',
        subtitle: 'Hub Central A2A',
        body: '',
        type: 'directory',
        status: this.channelConnected ? 'connected' : 'idle',
        x: cx - 240, y: cy - 150, w: 480, h: 300
      }
    ];

    agents.forEach((agent, index) => {
      const angleDeg = -90 + index * (360 / visibleCount);
      const angleRad = angleDeg * (Math.PI / 180);
      const ax = cx + radius * Math.cos(angleRad);
      const ay = cy + radius * Math.sin(angleRad);
      const isLocal = this.agents.some((local) => local.id === agent.agentId);
      nodes.push({
        id: `agent:${agent.agentId}`,
        title: agent.name,
        subtitle: agent.activity,
        body: agent.description || agent.specialty || agent.purpose || '',
        type: agent.agentId === 'decision-agent' ? 'decision' : isLocal ? 'agent' : 'external',
        status: agent.connected ? 'online' : 'down',
        x: Math.round(ax - nodeW / 2),
        y: Math.round(ay - nodeH / 2),
        w: nodeW,
        h: nodeH,
        compact: true,
        agentId: agent.agentId
      });
    });

    return nodes;
  }

  graphConnections(): VisualConnection[] {
    const connections: VisualConnection[] = [];

    for (const agent of this.filteredAgentsForDisplay()) {
      const msgCount = this.directMessageCount(agent.agentId);
      connections.push({
        id: `dir-${agent.agentId}`,
        from: 'directory',
        to: `agent:${agent.agentId}`,
        label: agent.agentId,
        kind: agent.agentId === 'decision-agent' ? 'agent' : agent.connected ? 'agent' : 'event',
        active: agent.connected,
        count: msgCount > 0 ? msgCount : undefined
      });
    }

    return connections;
  }

  connectionPath(connection: VisualConnection) {
    const nodes = this.graphNodes();
    const from = nodes.find((node) => node.id === connection.from);
    const to = nodes.find((node) => node.id === connection.to);
    if (!from || !to) return '';

    const fromCX = from.x + from.w / 2;
    const fromCY = from.y + from.h / 2;
    const toCX = to.x + to.w / 2;
    const toCY = to.y + to.h / 2;
    const dx = toCX - fromCX;
    const dy = toCY - fromCY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return '';

    const ux = dx / dist;
    const uy = dy / dist;
    const p1 = this.nodeEdgePoint(from, ux, uy);
    const p2 = this.nodeEdgePoint(to, -ux, -uy);
    const bend = Math.max(40, dist * 0.3);
    return `M ${p1.x} ${p1.y} C ${p1.x + ux * bend} ${p1.y + uy * bend}, ${p2.x - ux * bend} ${p2.y - uy * bend}, ${p2.x} ${p2.y}`;
  }

  private nodeEdgePoint(node: VisualNode, ux: number, uy: number) {
    const hw = node.w / 2;
    const hh = node.h / 2;
    const absUx = Math.abs(ux);
    const absUy = Math.abs(uy);
    const scale = absUx < 0.0001
      ? hh / Math.max(absUy, 0.0001)
      : absUy < 0.0001
        ? hw / absUx
        : Math.min(hw / absUx, hh / absUy);
    return { x: node.x + hw + ux * scale, y: node.y + hh + uy * scale };
  }

  get activeSpeaker() {
    return this.state?.typingAgent ?? this.state?.nextSpeaker ?? this.starter;
  }

  get activeReceiver(): AgentId {
    const messages = this.state?.messages ?? [];
    const last = messages[messages.length - 1];
    if (last?.from === this.activeSpeaker) return last.to;
    return this.activeSpeaker === 'agent-a' ? 'agent-b' : 'agent-a';
  }

  get channelConnected() {
    return this.state?.channelStatus === 'connected';
  }

  get connectionHealth() {
    if (this.socketStatus !== 'connected') return 'socket desconectado';
    if (!this.channelConnected) return 'canal A2A desconectado';
    return 'tiempo real conectado';
  }

  get totalMessageCount() {
    return (this.state?.directMessages?.length ?? 0) + (this.state?.messages?.length ?? 0);
  }

  get hubCX() { return 950; }
  get hubCY() { return 660; }

  private visibleMapViewport() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isCompact = width <= 980;
    const left = isCompact ? 12 : 278;
    const right = isCompact ? 12 : (this.selectedAgent() ? 396 : 18);
    const top = isCompact ? 92 : 112;
    const bottom = isCompact ? 138 : 118;

    return {
      left,
      top,
      width: Math.max(320, width - left - right),
      height: Math.max(260, height - top - bottom)
    };
  }

  private centerStageOn(stageX: number, stageY: number) {
    const viewport = this.visibleMapViewport();
    this.panX = Math.round(viewport.left + viewport.width / 2 - stageX * this.zoom);
    this.panY = Math.round(viewport.top + viewport.height / 2 - stageY * this.zoom);
  }

  connectionMid(connection: VisualConnection) {
    const nodes = this.graphNodes();
    const from = nodes.find((n) => n.id === connection.from);
    const to = nodes.find((n) => n.id === connection.to);
    if (!from || !to) return { x: 0, y: 0 };
    return {
      x: (from.x + from.w / 2 + to.x + to.w / 2) / 2,
      y: (from.y + from.h / 2 + to.y + to.h / 2) / 2
    };
  }

  formatTime(date: Date) {
    return date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  private trackConnectionEvents() {
    const agents = this.allAgents();
    const nowConnected = new Set(agents.filter((agent) => agent.connected).map((agent) => agent.agentId));
    const now = new Date();

    for (const agent of agents) {
      this.lastKnownAgents.set(agent.agentId, agent);
      if (agent.connected) this.agentSlot(agent.agentId);
    }

    // Stamp currently connected agents on first load so they appear in the ring
    for (const agentId of nowConnected) {
      if (!this.lastSeenAt.has(agentId)) {
        this.lastSeenAt.set(agentId, now);
      }
    }

    // New connections
    for (const agentId of nowConnected) {
      if (!this.prevConnectedIds.has(agentId)) {
        const entry = this.agentEntry(agentId);
        this.lastSeenAt.set(agentId, now);
        this.recentEvents = [{
          id: `${agentId}-${Date.now()}`,
          agentId,
          name: entry?.name ?? agentId,
          action: 'connected' as const,
          time: now
        }, ...this.recentEvents].slice(0, 10);
      }
    }

    // Disconnections
    for (const agentId of this.prevConnectedIds) {
      if (!nowConnected.has(agentId)) {
        const entry = this.agentEntry(agentId);
        const cached = this.lastKnownAgents.get(agentId);
        if (cached) {
          this.lastKnownAgents.set(agentId, {
            ...cached,
            connectionId: null,
            status: 'down',
            activity: 'desconectado',
            connected: false,
            available: false
          });
        }
        this.lastSeenAt.set(agentId, now);
        this.recentEvents = [{
          id: `${agentId}-${Date.now()}`,
          agentId,
          name: entry?.name ?? agentId,
          action: 'disconnected' as const,
          time: now
        }, ...this.recentEvents].slice(0, 10);
      }
    }

    this.prevConnectedIds = nowConnected;
  }

  private agentSlot(agentId: AgentId) {
    const existing = this.layoutSlots.get(agentId);
    if (existing !== undefined) return existing;

    const slot = this.nextLayoutSlot;
    this.layoutSlots.set(agentId, slot);
    this.nextLayoutSlot += 1;
    return slot;
  }

  private ensureSelectedAgent() {
    const agents = this.filteredAgentsForDisplay();
    if (!agents.length) return;
    if (!this.selectedAgentId || !agents.some((agent) => agent.agentId === this.selectedAgentId)) {
      this.selectedAgentId = agents[0].agentId;
      requestAnimationFrame(() => this.resetView());
    }
  }
}
